import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import { Plus, ShieldAlert, ArrowRight } from "lucide-react";
import AppHeader from "@/components/AppHeader";
import { Button } from "@/components/ui/button";
import { C, STATUSES, SHORT, money0, days, LEVEL, LEVEL_DOT } from "@/lib/claims";
import { TypePill } from "@/components/claims/ClaimBadges";

const Stat = ({ label, value, to, tone = "text-slate-100", testid, sub }) => (
  <Link to={to} data-testid={testid} className="rounded-xl border border-white/[0.06] bg-[#151D2E] px-4 py-3 hover:border-rose-400/50 transition-colors block min-w-0">
    <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold leading-tight truncate">{label}</div>
    <div className={`font-mono-num text-2xl font-black mt-1 ${tone}`}>{value}</div>
    {sub && <div className="text-[11px] text-slate-500">{sub}</div>}
  </Link>
);

function Attention({ rows }) {
  const navigate = useNavigate();
  return (
    <div className="rounded-xl border border-rose-500/25 bg-[#151D2E] p-4" data-testid="claims-attention">
      <div className="flex items-center gap-2 mb-2"><ShieldAlert className="w-4 h-4 text-rose-300" /><h2 className="text-[11px] uppercase tracking-[0.14em] font-bold text-rose-300">Needs attention</h2><span className="text-[11px] text-slate-500 ml-auto">longest at stage first</span></div>
      {rows.length === 0 ? <p className="text-sm text-slate-400">Nothing is stuck — every active claim is within its expected time at stage.</p> : (
        <ul className="divide-y divide-slate-800/70">
          {rows.map((c) => (
            <li key={c.id} onClick={() => navigate(`/claims/${c.id}`)} data-testid={`attention-${c.id}`} className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2 cursor-pointer hover:bg-white/[0.03] rounded-lg px-2 -mx-1 transition-colors">
              <span className={`w-2.5 h-2.5 rounded-full ${LEVEL_DOT[c.aging.level]} ${c.aging.level === "alert" ? "animate-pulse" : ""}`} />
              <span className="font-mono-num font-bold text-slate-100">{c.plate}</span>
              <span className="text-xs text-slate-400 truncate max-w-[160px]">{c.customer_name}</span>
              <TypePill type={c.claim_type} />
              <span className="text-sm text-slate-200">{c.current_status}</span>
              <span className={`font-mono-num text-sm font-bold ${LEVEL[c.aging.level]}`}>— {days(c.aging.days_at_stage)}</span>
              {c.insurer && <span className="text-xs text-slate-500">· {c.insurer}</span>}
              <ArrowRight className="w-3.5 h-3.5 text-slate-600 ml-auto" />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function ClaimsDashboard() {
  const [d, setD] = useState(null);
  useEffect(() => { axios.get(`${C}/dashboard`).then(({ data }) => setD(data)).catch(() => setD({ total: 0, total_active: 0, own_damage: 0, third_party: 0, payment_pending: 0, payment_received: 0, outstanding_total: 0, received_total: 0, by_status: {}, attention: [] })); }, []);
  const q = (s) => `/claims/list?status=${encodeURIComponent(s)}`;
  return (
    <div className="min-h-screen bg-[#0B0F17] text-slate-100 pb-24 md:pb-8">
      <AppHeader />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-5" data-testid="claims-dashboard">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div><p className="text-[10px] uppercase tracking-[0.2em] text-rose-400 font-bold">Insurance Claims</p><h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tight mt-1">Which claims are stuck?</h1><p className="text-slate-400 text-xs sm:text-sm mt-1">Motor accident claims from accident to insurer payment — Own Damage and Third Party, tracked stage by stage.</p></div>
          <div className="flex gap-2"><Link to="/claims/list"><Button variant="outline" data-testid="claims-view-all" className="h-9 border-slate-700 bg-transparent text-slate-200 hover:bg-slate-800">All claims</Button></Link><Link to="/claims/new"><Button data-testid="claims-new-btn" className="h-9 bg-rose-500 hover:bg-rose-400 text-slate-950 font-bold"><Plus className="w-4 h-4 mr-1.5" /> New claim</Button></Link></div>
        </div>
        {d && (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
              <Stat label="Total active claims" value={d.total_active} to="/claims/list?payment_status=not_received" tone="text-rose-300" testid="stat-active" sub={`${d.total} total · ${d.payment_received} closed`} />
              <Stat label="Own Damage" value={d.own_damage} to="/claims/list?claim_type=own_damage" tone="text-sky-300" testid="stat-od" />
              <Stat label="Third Party" value={d.third_party} to="/claims/list?claim_type=third_party" tone="text-fuchsia-300" testid="stat-tp" />
              <Stat label="Payment pending" value={d.payment_pending} to={q(STATUSES[4])} tone="text-amber-300" testid="stat-payment-pending" sub={`Outstanding ${money0(d.outstanding_total)}`} />
              <Stat label="Payment received" value={d.payment_received} to="/claims/list?payment_status=received" tone="text-emerald-300" testid="stat-payment-received" sub={`${money0(d.received_total)} banked`} />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500 font-bold mb-2">Active claims by stage</div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2" data-testid="claims-by-status">
                {STATUSES.map((s, i) => { const n = d.by_status[s] || 0; return (
                  <Link key={s} to={q(s)} data-testid={`stage-${i}`} className={`rounded-xl border px-3 py-2.5 transition-colors min-w-0 ${n ? "border-white/[0.08] bg-[#151D2E] hover:border-rose-400/50" : "border-white/[0.04] bg-slate-950/30 text-slate-600"}`}>
                    <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-bold text-slate-500"><span className="font-mono-num text-slate-600">{i + 1}</span>{SHORT[s]}</div>
                    <div className={`font-mono-num text-2xl font-black ${n ? "text-slate-100" : "text-slate-700"}`}>{n}</div>
                    <div className="text-[10px] text-slate-500 leading-tight truncate">{s}</div>
                  </Link>); })}
              </div>
            </div>
            <Attention rows={d.attention} />
          </>
        )}
      </main>
    </div>
  );
}
